import { AssessmentResultType, PassFailResult, TemplateKey } from '../../../apolloClient.types';
import { PassFailWithNull } from '../Soc/SocFormConfig';
import type { AssessmentResultGroupFragment } from './AssessmentResults.api';

export interface FilterState {
    programId?: string;
    assessmentRunId?: string;
    userName?: string;
    groupDetail?: AssessmentResultGroupFragment;
    moduleInstances: ModuleDetail[];
}

export interface ModuleDetail {
    id: string;
    name: string;
    moduleDefinitionIdentifier: string;
}

export interface TabRef {
    refetch: () => void;
}

export const validModuleDefinitions = [
    'ippt',
    'strength-assessment',
    '20km-road-march',
    'soc',
    'cardio-respiratory',
    'weight',
    'fin-swim',
    'bmp',
];

export const getModuleDefinitionIdentifierByResultType = (type: string): string => {
    switch (type) {
        case AssessmentResultType.Ippt:
            return 'ippt';
        case AssessmentResultType.Soc:
            return 'soc';
        case AssessmentResultType.March20km:
            return '20km-road-march';
        case AssessmentResultType.Weight:
            return 'weight';
        case AssessmentResultType.CardioResp:
            return 'cardio-respiratory';
        case AssessmentResultType.Strength:
            return 'strength-assessment';
        case AssessmentResultType.FinSwim:
            return 'fin-swim';
        case AssessmentResultType.Bmp:
            return 'bmp';
        default:
            return '';
    }
};

export const getTemplateKeyByResultType = (resultType: AssessmentResultType): TemplateKey => {
    switch (resultType) {
        case AssessmentResultType.Ippt:
            return TemplateKey.IpptImport;
        case AssessmentResultType.Soc:
            return TemplateKey.SocImport;
        case AssessmentResultType.March20km:
            return TemplateKey.March20kmImport;
        case AssessmentResultType.Weight:
            return TemplateKey.WeightImport;
        case AssessmentResultType.CardioResp:
            return TemplateKey.CardioRespImport;
        case AssessmentResultType.Strength:
            return TemplateKey.StrengthImport;
        case AssessmentResultType.FinSwim:
            return TemplateKey.FinSwimImport;
        case AssessmentResultType.Bmp:
            return TemplateKey.BmpImport;
        default:
            return TemplateKey.IpptImport; // Should not happen
    }
};

export const getAssessmentResultTypeLabel = (resultType: AssessmentResultType): string => {
    switch (resultType) {
        case AssessmentResultType.Ippt:
            return 'IPPT';
        case AssessmentResultType.Soc:
            return 'SOC';
        case AssessmentResultType.March20km:
            return '20km Combat March';
        case AssessmentResultType.Weight:
            return 'Weight';
        case AssessmentResultType.CardioResp:
            return 'Cardio Respiratory';
        case AssessmentResultType.Strength:
            return 'Strength';
        case AssessmentResultType.FinSwim:
            return 'FIN Swim';
        case AssessmentResultType.Bmp:
            return 'BMP';
        default:
            return '';
    }
};

/**
 * Map SOC form radio value ("Pass" | "Fail" | '' | null | undefined)
 * to GraphQL enum PassFailResult or undefined
 */
export const mapPassFailWithNullToResult = (value?: string | null): PassFailResult | undefined => {
    if (value === PassFailResult.Pass) {
        return PassFailResult.Pass;
    } else if (value === PassFailResult.Fail) {
        return PassFailResult.Fail;
    } else {
        return undefined;
    }
};

/**
 * Map backend enum (PassFailResult | null/undefined) to the form's native enum union ("Pass" | "Fail" | '')
 * Used for default values in forms that use a radio option for NA as ''
 */
export const mapResultToPassFailWithNull = (value?: PassFailResult | null): PassFailWithNull => {
    if (value === PassFailResult.Pass) {
        return PassFailWithNull.Pass;
    } else if (value === PassFailResult.Fail) {
        return PassFailWithNull.Fail;
    } else {
        return PassFailWithNull.Null;
    }
};

/**
 * Format seconds as minutes and seconds in string
 * @param seconds - Time in seconds
 * @returns Time in minutes and seconds in string
 */
export const formatSecondsAsMinSec = (seconds?: number | null): string | null => {
    if (seconds === null || seconds === undefined || Number.isNaN(seconds)) {
        return null;
    }
    const total = Math.max(0, Math.floor(seconds));
    const minutes = Math.floor(total / 60);
    const secs = total % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Split seconds into minutes and seconds
 * @param seconds - Time in seconds
 * @returns Time in minutes and seconds
 */
export const splitSeconds = (seconds?: number | null): { minutes: number | null; seconds: number | null } => {
    if (seconds === null || seconds === undefined || Number.isNaN(seconds)) {
        return { minutes: null, seconds: null };
    }
    const total = Math.max(0, Math.floor(seconds));
    const minutes = Math.floor(total / 60);
    const secs = total % 60;
    return { minutes, seconds: secs };
};

/**
 * Format seconds as hours and minutes in string (H:MM)
 * @param seconds - Time in seconds
 * @returns Time in hours and minutes as H:MM, or null if invalid
 */
export const formatSecondsAsHoursMin = (seconds?: number | null): string | null => {
    if (seconds === null || seconds === undefined || Number.isNaN(seconds)) {
        return null;
    }
    const total = Math.max(0, Math.floor(seconds));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
};

/**
 * Split seconds into hours and minutes
 * @param seconds - Time in seconds
 * @returns Hours and minutes object, or nulls if invalid
 */
export const splitSecondsToHoursMinutes = (
    seconds?: number | null
): { hours: number | null; minutes: number | null } => {
    if (seconds === null || seconds === undefined || Number.isNaN(seconds)) {
        return { hours: null, minutes: null };
    }
    const total = Math.max(0, Math.floor(seconds));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    return { hours, minutes };
};
